import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, Eye, EyeOff, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) {
    navigate("/login?redirect=/change-password");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (next.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (current === next) {
      setError("New password must be different from current password.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      setSuccess(true);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to change password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-6">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Lock className="h-5 w-5 text-[#39FF14]" strokeWidth={1.5} />
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14]">Account</p>
        </div>
        <h1 data-testid="change-password-heading" className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Change Password
        </h1>
        <p className="text-[#A1A1A1] text-sm mb-8">Signed in as <span className="font-mono text-[#F2F2F2]">{user.email}</span></p>

        {success && (
          <div className="mb-6 p-4 border border-[#39FF14]/30 bg-[#39FF14]/5 flex items-start gap-3" data-testid="change-password-success">
            <Check className="h-4 w-4 text-[#39FF14] shrink-0 mt-0.5" />
            <div>
              <p className="text-[#39FF14] text-sm font-bold">Password changed</p>
              <p className="text-[#A1A1A1] text-xs mt-1">Use your new password the next time you sign in.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="change-password-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-form">
          <div>
            <Label htmlFor="cp-current" className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Current password</Label>
            <div className="relative mt-1">
              <Input
                id="cp-current"
                data-testid="current-password"
                type={showCurrent ? "text" : "password"}
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono pr-10"
              />
              <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1A1] hover:text-[#39FF14]" tabIndex={-1}>
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="cp-new" className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">New password</Label>
            <div className="relative mt-1">
              <Input
                id="cp-new"
                data-testid="new-password"
                type={showNext ? "text" : "password"}
                required
                minLength={6}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono pr-10"
              />
              <button type="button" onClick={() => setShowNext(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1A1] hover:text-[#39FF14]" tabIndex={-1}>
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[#A1A1A1]/60 text-[10px] font-mono mt-1">Min 6 characters. Use a mix of letters, numbers, symbols.</p>
          </div>

          <div>
            <Label htmlFor="cp-confirm" className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Confirm new password</Label>
            <Input
              id="cp-confirm"
              data-testid="confirm-password"
              type={showNext ? "text" : "password"}
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="change-password-submit"
            className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-5 disabled:opacity-50"
          >
            {submitting ? "Updating..." : "Change Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
